/**
 * Settings & Admin → Module Setup (Phase 0).
 *
 * Surfaces the config-key module-relevance matrix (/v1/config-catalog): for each
 * module, exactly what's REQUIRED vs OPTIONAL - the "minimal needed per module"
 * bifurcation. Each row links to where it's configured + shows where its value
 * resolves from (local / inherited-from-Core / adapter) and the inherit policy.
 */

import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { CheckCircle2, CircleDashed, ExternalLink, Radar, Activity as ActivityIcon, ShieldAlert, ScrollText } from 'lucide-react';
import { GlassCard } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { getConfigCatalog, type ConfigCatalogKey, type ConfigModuleKey, type ConfigReadiness } from '@/lib/api';

const MODULE_META: Record<ConfigModuleKey, { label: string; icon: React.ComponentType<{ className?: string }>; tone: string }> = {
  discovery: { label: 'Discovery (DSPM)', icon: Radar, tone: 'text-info' },
  activity: { label: 'Activity (DAM)', icon: ActivityIcon, tone: 'text-success' },
  detection: { label: 'Detection (DDR)', icon: ShieldAlert, tone: 'text-warning' },
  compliance: { label: 'Compliance', icon: ScrollText, tone: 'text-primary' },
};
const MODULE_ORDER: ConfigModuleKey[] = ['discovery', 'activity', 'detection', 'compliance'];

const SOURCE_LABEL: Record<string, string> = {
  local: 'Local',
  federates: 'Inherits from Core',
  adapter: 'Adapter',
};

export function AdminModuleSetup(): JSX.Element {
  const [catalog, setCatalog] = useState<ConfigCatalogKey[]>([]);
  const [byModule, setByModule] = useState<Record<ConfigModuleKey, { required: string[]; optional: string[] }> | null>(null);
  const [readiness, setReadiness] = useState<Record<string, ConfigReadiness>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    void getConfigCatalog()
      .then((r) => {
        if (ctrl.signal.aborted) return;
        setCatalog(r.catalog);
        setByModule(r.byModule);
        setReadiness(r.readiness ?? {});
        setError(null);
      })
      .catch((e) => {
        if (!ctrl.signal.aborted) setError(e instanceof Error ? e.message : 'Failed to load');
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false);
      });
    return () => ctrl.abort();
  }, []);

  const keyOf = (k: string): ConfigCatalogKey | undefined => catalog.find((c) => c.key === k);

  function Row({ k, required }: { k: ConfigCatalogKey; required: boolean }): JSX.Element {
    const state: ConfigReadiness = readiness[k.key] ?? 'unknown';
    const Icon = state === 'yes' ? CheckCircle2 : CircleDashed;
    const iconCls =
      state === 'yes' ? 'text-success' : state === 'no' && required ? 'text-warning' : 'text-muted-foreground';
    return (
      <Link to={k.href} className="flex items-start gap-2.5 rounded-md px-2.5 py-2 hover:bg-muted/30 transition-colors">
        <Icon className={`h-4 w-4 shrink-0 mt-0.5 ${iconCls}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium">{k.label}</span>
            <ExternalLink className="h-3 w-3 text-muted-foreground" />
            {state === 'yes' && <Badge variant="success">configured</Badge>}
            {state === 'no' && required && <Badge variant="warning">needs setup</Badge>}
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5">{k.blurb}</p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <Badge variant={k.source === 'federates' ? 'info' : 'glass'}>{SOURCE_LABEL[k.source] ?? k.source}</Badge>
          {k.inheritPolicy === 'accept-gate' && <Badge variant="warning">accept-gate</Badge>}
        </div>
      </Link>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Module Setup"
        subtitle="What each module minimally needs - configure once, applies everywhere. Required items must be set for the module to operate; optional items enrich it. Items marked 'Inherits from Core' are filled automatically when paired."
        badges={[{ label: 'Configure once', variant: 'glass' }, { label: 'Module-aware', variant: 'info' }]}
      />

      {loading && <LoadingState caption="Loading the configuration matrix…" />}
      {error && !loading && <ErrorState title="Could not load configuration matrix" detail={error} />}

      {!loading && byModule && (
        <div className="grid gap-4 md:grid-cols-2">
          {MODULE_ORDER.map((m) => {
            const meta = MODULE_META[m];
            const Icon = meta.icon;
            const req = byModule[m].required.map(keyOf).filter(Boolean) as ConfigCatalogKey[];
            const opt = byModule[m].optional.map(keyOf).filter(Boolean) as ConfigCatalogKey[];
            const reqChecked = req.filter((k) => readiness[k.key] !== 'unknown');
            const reqReady = reqChecked.filter((k) => readiness[k.key] === 'yes').length;
            return (
              <GlassCard key={m} className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Icon className={`h-5 w-5 ${meta.tone}`} />
                  <h2 className="text-sm font-semibold">{meta.label}</h2>
                  <span className="ml-auto text-[11px] text-muted-foreground">
                    {reqChecked.length > 0 && (
                      <span className={reqReady === reqChecked.length ? 'text-success' : 'text-warning'}>
                        {reqReady}/{reqChecked.length} ready ·{' '}
                      </span>
                    )}
                    {req.length} required · {opt.length} optional
                  </span>
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 px-2.5">Required</div>
                    {req.length === 0 ? (
                      <p className="text-[11px] text-muted-foreground px-2.5">None.</p>
                    ) : (
                      req.map((k) => <Row key={k.key} k={k} required />)
                    )}
                  </div>
                  {opt.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 px-2.5">Optional</div>
                      {opt.map((k) => <Row key={k.key} k={k} required={false} />)}
                    </div>
                  )}
                </div>
              </GlassCard>
            );
          })}
        </div>
      )}
    </PageContainer>
  );
}
